/**
 * packages/server/src/crypto.ts
 *
 * AES-256-GCM 암호화/복호화 — API Key 등 민감 데이터 보호.
 *
 * 암호화 키: .gencow/secret.key (자동 생성, 32 bytes)
 * 알고리즘: AES-256-GCM (인증 태그 포함 → 변조 탐지)
 */
import { createCipheriv, createDecipheriv, randomBytes } from "crypto";
import { readFileSync, writeFileSync, mkdirSync, existsSync } from "fs";
import { resolve, dirname } from "path";

const ALGORITHM = "aes-256-gcm";
const KEY_LENGTH = 32; // 256 bits
const IV_LENGTH = 16; // 128 bits
const AUTH_TAG_LENGTH = 16; // 128 bits

// ─── Secret Key Management ──────────────────────────────────

let cachedKey: Buffer | null = null;

/**
 * .gencow/secret.key 경로 결정
 * 환경변수 GENCOW_SECRET_KEY_PATH 또는 프로젝트 루트의 .gencow/secret.key
 */
function getSecretKeyPath(): string {
  if (process.env.GENCOW_SECRET_KEY_PATH) {
    return process.env.GENCOW_SECRET_KEY_PATH;
  }
  // 프로젝트 루트 = CWD (CLI가 프로젝트 루트에서 실행)
  return resolve(process.cwd(), ".gencow", "secret.key");
}

/**
 * 암호화 키 로드
 *
 * BaaS 모드: GENCOW_INTERNAL_TOKEN에서 deterministic key 파생 (파일 불필요)
 *   → cowbox 샌드박스에서 .gencow/ 디렉토리 쓰기 불가하므로 파일 기반 key 사용 불가
 * 로컬 dev: 기존 파일 기반 key 관리 (.gencow/secret.key 자동 생성)
 */
export function getSecretKey(): Buffer {
  if (cachedKey) return cachedKey;

  // BaaS 모드: GENCOW_INTERNAL_TOKEN → deterministic HMAC key 파생
  // cowbox 환경에서는 .gencow/ 디렉토리에 쓰기 불가 → 파일 기반 key 불가
  const internalToken = process.env.GENCOW_INTERNAL_TOKEN;
  if (internalToken) {
    const { createHash } = require("crypto");
    cachedKey = createHash("sha256")
      .update(`gencow-settings-key:${internalToken}`)
      .digest()
      .subarray(0, KEY_LENGTH) as Buffer;
    return cachedKey;
  }

  // 로컬 dev: 기존 파일 기반 키 관리
  const keyPath = getSecretKeyPath();

  try {
    cachedKey = readFileSync(keyPath);
    if (cachedKey.length !== KEY_LENGTH) {
      throw new Error(`Invalid key length: ${cachedKey.length} (expected ${KEY_LENGTH})`);
    }
    return cachedKey;
  } catch {
    // 키 파일이 없으면 자동 생성
    try {
      const dir = dirname(keyPath);
      if (!existsSync(dir)) {
        mkdirSync(dir, { recursive: true });
      }

      const newKey = randomBytes(KEY_LENGTH);
      writeFileSync(keyPath, newKey, { mode: 0o600 }); // owner-only read/write
      console.log(`[crypto] Generated secret key: ${keyPath}`);

      cachedKey = newKey;
      return cachedKey;
    } catch {
      // fallback: 메모리 전용 키 (서버 재시작 시 변경됨)
      // cowbox 외부에서 파일 쓰기 실패 시에도 서버는 시작 가능
      console.warn(`[crypto] Cannot write secret.key — using ephemeral key (settings will reset on restart)`);
      cachedKey = randomBytes(KEY_LENGTH);
      return cachedKey;
    }
  }
}

// ─── Encrypt / Decrypt ──────────────────────────────────────

/**
 * 평문을 AES-256-GCM으로 암호화
 * @returns { encrypted: hex string, iv: hex string }
 */
export function encrypt(plaintext: string): { encrypted: string; iv: string } {
  const key = getSecretKey();
  const iv = randomBytes(IV_LENGTH);

  const cipher = createCipheriv(ALGORITHM, key, iv, { authTagLength: AUTH_TAG_LENGTH });
  let encrypted = cipher.update(plaintext, "utf8", "hex");
  encrypted += cipher.final("hex");

  // authTag를 encrypted에 append (복호화 시 분리)
  const authTag = cipher.getAuthTag().toString("hex");
  encrypted += authTag;

  return {
    encrypted,
    iv: iv.toString("hex"),
  };
}

/**
 * AES-256-GCM 복호화
 */
export function decrypt(encryptedHex: string, ivHex: string): string {
  const key = getSecretKey();
  const iv = Buffer.from(ivHex, "hex");

  // authTag는 encrypted 끝 32자 (16 bytes hex)
  const authTagHex = encryptedHex.slice(-AUTH_TAG_LENGTH * 2);
  const cipherTextHex = encryptedHex.slice(0, -AUTH_TAG_LENGTH * 2);

  const decipher = createDecipheriv(ALGORITHM, key, iv, { authTagLength: AUTH_TAG_LENGTH });
  decipher.setAuthTag(Buffer.from(authTagHex, "hex"));

  let decrypted = decipher.update(cipherTextHex, "hex", "utf8");
  decrypted += decipher.final("utf8");

  return decrypted;
}

/**
 * API Key 마스킹 — 앞 8자만 표시, 나머지는 ••••
 * @example maskKey("sk-proj-abc123xyz") → "sk-proj-••••••••"
 */
export function maskKey(key: string): string {
  if (key.length <= 8) return "••••••••";
  return key.slice(0, 8) + "••••••••";
}
